import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Typeahead, Input, Button } from '../../components';
import { getClasses, createClass } from '@/services/ClassService';
import { useBts } from '@/context/btsContext/BtsContextProvider';
import ProposalProductTable from './ProposalProductTable';
import { showAlert } from '../../components/common/toastify/ToastContainer';
import { useDebouncedCallback } from '@/utils/useDebouncedCallback';
import { useProduct } from '../../context/productContext/ProductContextProvider';
import CreateProduct from '../setting/product/CreateProduct';
import { ModalProvider } from '../../components/common/modal/ModalContext';
import { useModal } from '../../components/common/modal/ModalContext';
import { MessageIconButton } from '@jaybel/chat-lib';
import { verifyProductCsvFile } from '@/services/ProductService';
import NotesParentsSection from '../../components/common/notes/NotesParentsSection';
import { getNotesParentsBts, saveNotesParentsBts, deleteNotesParentsBts, getClassDivisions } from '@/services/BtsService';

const VerificationReportModal = ({ existingCount, nonExistingProducts, onConfirm, onCancel }) => {
  const handleDownloadCsv = () => {
    const csvHeaders = 'Product Code,Class Name,Reason\n';
    const csvRows = nonExistingProducts
      .map(prod => {
        const cleanCode = (prod.csvProductCode || '').replace(/"/g, '""');
        const cleanClass = (prod.className || 'Default').replace(/"/g, '""');
        const cleanReason = (prod.csvDescription || '').replace(/"/g, '""');
        return `"${cleanCode}","${cleanClass}","${cleanReason}"`;
      })
      .join('\n');
    const blob = new Blob([csvHeaders + csvRows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'non_existing_products.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-4 max-w-2xl">
      <div className="mb-4 text-sm text-gray-600">
        We verified the uploaded file and found the following results:
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center">
          <div className="text-2xl font-bold text-green-600">{existingCount}</div>
          <div className="text-xs text-gray-500 font-medium">Valid BTS Products</div>
        </div>
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-center">
          <div className="text-2xl font-bold text-red-600">{nonExistingProducts.length}</div>
          <div className="text-xs text-gray-500 font-medium">Non-Existing / Invalid</div>
        </div>
      </div>

      <div className="mb-4">
        <div className="text-sm font-semibold text-red-600 mb-2 flex items-center justify-between">
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Warning: The following products will be skipped during upload
          </span>
          <Button
            type="button"
            onClick={handleDownloadCsv}
            className="secondary"
            style={{ padding: '4px 12px', fontSize: '12px' }}
          >
            Download CSV
          </Button>
        </div>

        <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg shadow-inner bg-gray-50">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider font-semibold">
                <th className="px-4 py-2">Product Code</th>
                <th className="px-4 py-2">Class Name</th>
                <th className="px-4 py-2">Reason</th>
              </tr>
            </thead>
            <tbody>
              {nonExistingProducts.map((prod, index) => (
                <tr key={index} className="border-b border-gray-200 hover:bg-gray-100 transition-colors">
                  <td className="px-4 py-2 font-mono text-xs font-semibold text-red-600 bg-red-50/30">{prod.csvProductCode}</td>
                  <td className="px-4 py-2 text-xs text-gray-700">{prod.className || 'Default'}</td>
                  <td className="px-4 py-2 text-xs text-red-500 font-medium">{prod.csvDescription || 'Validation issue'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-sm text-gray-500 mb-6 leading-relaxed">
        If you click <span className="font-semibold text-gray-700">Proceed anyway</span>, only the <span className="font-semibold text-green-600">{existingCount} valid products</span> will be imported into the class, and the invalid products listed above will be ignored.
      </div>

      <div className="flex justify-end gap-3 border-t pt-4">
        <Button
          type="button"
          onClick={onCancel}
          className="secondary"
          style={{ padding: '6px 16px', fontSize: '14px' }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onConfirm}
          style={{ padding: '6px 16px', fontSize: '14px' }}
        >
          Proceed anyway
        </Button>
      </div>
    </div>
  );
};

/**
 * Modal shown AFTER a successful upload when some rows were skipped due to errors.
 * Props:
 *   insertedCount   - how many rows were newly inserted
 *   duplicateCount  - how many rows were silently skipped (already exist in that class)
 *   skippedProducts - array of { productCode, className, reason } — ERRORS only
 *   onClose         - close handler
 */
const UploadSkippedModal = ({ insertedCount, duplicateCount, skippedProducts, onClose }) => {
  const handleDownloadCsv = () => {
    const csvHeaders = 'Product Code,Class Name,Reason\n';
    const csvRows = skippedProducts
      .map(p => {
        const code = (p.productCode || '').replace(/"/g, '""');
        const cls = (p.className || '').replace(/"/g, '""');
        const reason = (p.reason || '').replace(/"/g, '""');
        return `"${code}","${cls}","${reason}"`;
      })
      .join('\n');
    const blob = new Blob([csvHeaders + csvRows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'skipped_products.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-4 max-w-2xl">
      <div className="mb-4 text-sm text-gray-600">
        Upload complete. Here is a summary of the results:
      </div>

      {/* 3-column summary */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center">
          <div className="text-2xl font-bold text-green-600">{insertedCount - (duplicateCount ?? 0)}</div>
          <div className="text-xs text-gray-500 font-medium mt-1">✅ Newly Imported</div>
        </div>
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
          <div className="text-2xl font-bold text-yellow-600">{duplicateCount ?? 0}</div>
          <div className="text-xs text-gray-500 font-medium mt-1">⚠️ Already Existed</div>
          <div className="text-xs text-gray-400 mt-0.5">(added successfully)</div>
        </div>
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-center">
          <div className="text-2xl font-bold text-red-600">{skippedProducts.length}</div>
          <div className="text-xs text-gray-500 font-medium mt-1">❌ Errors (skipped)</div>
          <div className="text-xs text-gray-400 mt-0.5">(fix &amp; re-upload)</div>
        </div>
      </div>

      {skippedProducts.length > 0 && (
        <div className="mb-4">
          <div className="text-sm font-semibold text-red-600 mb-2 flex items-center justify-between">
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Products with Errors — not imported
            </span>
            <Button
              type="button"
              onClick={handleDownloadCsv}
              className="secondary"
              style={{ padding: '4px 12px', fontSize: '12px' }}
            >
              Download CSV
            </Button>
          </div>

          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg shadow-inner bg-gray-50">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider font-semibold">
                  <th className="px-4 py-2">Product Code</th>
                  <th className="px-4 py-2">Class</th>
                  <th className="px-4 py-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {skippedProducts.map((p, idx) => (
                  <tr key={idx} className="border-b border-gray-200 hover:bg-gray-100 transition-colors">
                    <td className="px-4 py-2 font-mono text-xs font-semibold text-red-600 bg-red-50/30">{p.productCode || '-'}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">{p.className || '-'}</td>
                    <td className="px-4 py-2 text-xs text-red-500 font-medium">{p.reason || 'Unknown error'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {skippedProducts.length === 0 && duplicateCount > 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          All {duplicateCount} duplicate products already existed and were added successfully.
        </div>
      )}

      <div className="flex justify-end gap-3 border-t pt-4">
        <Button
          type="button"
          onClick={onClose}
          style={{ padding: '6px 16px', fontSize: '14px' }}
        >
          Close
        </Button>
      </div>
    </div>
  );
};

const ProposalProduct = ({ proposalId, goToNextSection, isUploadWizard = false }) => {
  const { id } = useParams();
  const [classOptions, setClassOptions] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [productQuery, setProductQuery] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const fileInputRef = React.useRef();
  const productTypeaheadRef = React.useRef();
  const { openModal, closeModal } = useModal();
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [isVerifying, setIsVerifying] = useState(false);

  const {
    resetBtsProduct,
    btsProduct,
    setBtsProduct,
    btsProductInitialState,
    uploadBtsProductFileByClassHandler,
    uploadSchoolProposalCsvFileHandler,
    addManualProduct,
    updateManualProduct,
    fetchBtsProductsByClass, // <-- add this from context
    btsClassProdData,
    exportBTSExcelReport,
    exportParentsInstructionAPI,
    exportPdfAPI,
    exportBtsShReportAPI,
    getClassListForProposalAPI,
    updateClassStudentCount,
    btsSchool,
  } = useBts();

  const [numberOfStudents, setNumberOfStudents] = useState('');
  const [proposalClasses, setProposalClasses] = useState([]);

  const [isShExporting, setIsShExporting] = useState(false);
  const isProposalLoaded = Boolean(proposalId || id);

  const handleExportShReport = async () => {
    try {
      setIsShExporting(true);
      await exportBtsShReportAPI(proposalId || id); // your service already handles blob download
    } catch (e) {
      handleAlert('error', e);
    } finally {
      setIsShExporting(false);
    }
  };

  const { getAllProduct, proposalProducts } = useProduct();

  // Notes state
  const [notes, setNotes] = useState({ note_A: '', note_B: '' });
  const [isSavingNote, setIsSavingNote] = useState({ note_A: false, note_B: false });

  // Class Divisions state
  const [divisions, setDivisions] = useState([]);

  const fetchNotes = async () => {
    if (!selectedClass || (!proposalId && !id)) {
      setNotes({ note_A: '', note_B: '' });
      return;
    }
    try {
      const fetchedNotes = await getNotesParentsBts(proposalId || id, selectedClass.id);
      const notesMap = { note_A: '', note_B: '' };
      if (fetchedNotes && Array.isArray(fetchedNotes)) {
        fetchedNotes.forEach(n => {
          if (n.noteType === 'note_A' || n.noteType === 'note_B') {
            notesMap[n.noteType] = n.note || '';
          }
        });
      }
      setNotes(notesMap);
    } catch (err) {
      console.error("Failed to fetch notes", err);
    }
  };

  const fetchDivisions = async () => {
    if (!selectedClass || (!proposalId && !id)) {
      setDivisions([]);
      return;
    }
    try {
      const data = await getClassDivisions(proposalId || id, selectedClass.id);
      setDivisions(data || []);
    } catch (err) {
      console.error("Failed to fetch divisions", err);
      setDivisions([]);
    }
  };

  useEffect(() => {
    fetchNotes();
    fetchDivisions();
  }, [selectedClass, proposalId, id]);

  const handleSaveNote = async (noteType, noteContent) => {
    if (!selectedClass || (!proposalId && !id)) return;
    setIsSavingNote(prev => ({ ...prev, [noteType]: true }));
    try {
      if (noteContent === '') {
        await deleteNotesParentsBts(proposalId || id, selectedClass.id, noteType);
        setNotes(prev => ({ ...prev, [noteType]: '' }));
        showAlert('success', "Note deleted successfully");
      } else {
        await saveNotesParentsBts(proposalId || id, selectedClass.id, {
          noteType: noteType,
          note: noteContent
        });
        setNotes(prev => ({ ...prev, [noteType]: noteContent }));
        showAlert('success', "Note saved successfully");
      }
    } catch (err) {
      console.error("Failed to save/delete note", err);
      showAlert('error', "Failed to save/delete note");
    } finally {
      setIsSavingNote(prev => ({ ...prev, [noteType]: false }));
    }
  };

  const productOption = useMemo(() => {
    return proposalProducts?.map((product) => ({
      label:
        `${product.itemCode} - ${product.itemDescription} - ` +
        `${product.btsPrice == null ? '<strong>BTS Price does not exist</strong>' : product.btsPrice} ` +
        (product.inPronto === null || product.inPronto === undefined
          ? ''
          : product.inPronto
            ? ' <span style="float: right; color: green; font-weight: bold;">IN PRONTO</span>'
            : ' <span style="float: right; color: red; font-weight: bold;">NOT IN PRONTO</span>'),
      value: product.id,
      data: product,
    }));
  }, [proposalProducts]);

  useEffect(() => {
    if (productQuery.trim().length >= 3) {
      getAllProduct(productQuery);
    }
  }, [productQuery]);

  //debouncing
  const debouncedSetSearchQuery = useDebouncedCallback((val) => {
    setProductQuery(val);
  }, 300);

  const handleSearchChange = (query) => {
    debouncedSetSearchQuery.call(query);
  };

  useEffect(() => {
    fetchBtsProductsByClass(
      proposalId || id,
      selectedClass ? selectedClass.id : 0,
    );
  }, [selectedClass, proposalId, id]);

  // Fetch classes on mount
  useEffect(() => {
    getClasses()
      .then((res) =>
        setClassOptions(
          res.map((cls) => ({
            id: cls.id,
            label: cls.className,
            className: cls.className,
          })),
        ),
      )
      .catch(() => setClassOptions([]));
    resetBtsProduct();
  }, []);

  // Centralized alert handler
  const handleAlert = (type, error) => {
    if (type === 'success') return showAlert('success', error);
    if (error && error.response && error.response.data) {
      const data = error.response.data;
      if (Array.isArray(data.errors) && data.errors.length > 0)
        return showAlert('error', data.errors[0]);
      if (typeof data === 'string') return showAlert('error', data);
      if (data.message) return showAlert('error', data.message);
    }
    showAlert('error', error.message || 'Upload failed');
  };

  useEffect(() => {
    if (proposalId || id) {
      getClassListForProposalAPI(proposalId || id).then((data) => {
        setProposalClasses(data || []);
      });
    }
  }, [proposalId, id]);

  useEffect(() => {
    if (selectedClass) {
      const found = proposalClasses.find((c) => c.id === selectedClass.id);
      setNumberOfStudents(found ? found.numberOfStudents : '');
    } else {
      setNumberOfStudents('');
    }
  }, [selectedClass, proposalClasses]);

  const handleStudentCountChange = async (e) => {
    const val = e.target.value;
    setNumberOfStudents(val);
  };

  const handleStudentCountBlur = async () => {
    if (!selectedClass || !numberOfStudents) return;
    if (parseInt(numberOfStudents, 10) < 1) {
      handleAlert('error', { message: 'Number of students must be at least 1' });
      return;
    }
    try {
      await updateClassStudentCount(
        proposalId || id,
        selectedClass.id,
        numberOfStudents,
      );
      // Refresh class list to ensure sync
      const data = await getClassListForProposalAPI(proposalId || id);
      setProposalClasses(data || []);
      showAlert('success', 'Student count updated');
    } catch (e) {
      handleAlert('error', e);
    }
  };

  // Handlers
  const handleClassChange = (selected) => setSelectedClass(selected[0] || null);
  const handleCreateClass = async (newClassName) => {
    try {
      const res = await createClass({ className: newClassName });
      const newClass = {
        id: res.id,
        label: res.className,
        className: res.className,
      };
      setClassOptions((prev) => [...prev, newClass]);
      setSelectedClass(newClass);
      return newClass;
    } catch {
      handleAlert('error', { message: 'Failed to create class' });
      return null;
    }
  };
  const handleProductSelect = (selected) => {
    if (selected && selected.length > 0) {
      const prod = selected[0].data;
      setSelectedProduct([selected[0]]);
      // Set sequenceNumber to max length of btsClassProdData + 1
      setBtsProduct({
        id: 0,
        btsProductId: prod.id,
        schoolProposalId: 0,
        btsClassId: selectedClass ? selectedClass.id : 0,
        className: selectedClass ? selectedClass.className : '',
        itemCode: prod.itemCode,
        description: (prod.itemDescription || '') + (prod.description2 ? ' ' + prod.description2 : ''),
        taxCode: prod.taxCode,
        conversionFactor: prod.btsConversionFactor || 1,
        price: prod.btsPrice ?? 0,
        normalPrice: prod.normalPrice,
        unit: prod.unit?.trim() || 'Each',
        avgCost: prod.avgCost,
        promoPrice: prod.promoPrice,
        uploadTimestamp: prod.uploadTimestamp,
        sequenceNumber: 0,
        header: '',
      });
    }
  };
  const calculateSellValues = (
    gp,
    price,
    quantity,
    conversionFactor = 1,
    currentTaxCode,
    currentExSell,
    currentIncSell,
  ) => {
    let exSell = 0,
      incSell = 0,
      total = 0,
      totalIncSell = 0,
      totalExSell = 0;
    // Adjust price by conversionFactor
    const adjustedPrice =
      conversionFactor && conversionFactor > 0
        ? price / conversionFactor
        : price;
    const numericGp = parseFloat(gp);
    if (adjustedPrice > 0 && !isNaN(numericGp) && numericGp < 100) {
      exSell = Math.round((adjustedPrice / (1 - numericGp / 100)) * 10000) / 10000;
      const isExempt =
        currentTaxCode === 'E' ||
        (currentExSell != null &&
          currentIncSell != null &&
          Math.abs(Number(currentIncSell) - Number(currentExSell)) < 0.0001 &&
          Number(currentExSell) > 0);
      incSell = isExempt ? exSell : exSell * 1.1;
      totalExSell = exSell * (quantity || 1);
      totalIncSell = incSell * (quantity || 1);
      total = totalIncSell; // for compatibility if used elsewhere
    }
    return { exSell, incSell, total, totalIncSell, totalExSell };
  };
  const handleGpChange = (e) => {
    let gp = parseFloat(e.target.value);
    if (isNaN(gp)) gp = '';
    if (gp !== '' && gp > 100) gp = 100;
    const { exSell, incSell, total, totalIncSell, totalExSell } =
      calculateSellValues(
        gp,
        btsProduct.price,
        btsProduct.quantity,
        btsProduct.conversionFactor,
        btsProduct.taxCode,
        btsProduct.exSell,
        btsProduct.incSell,
      );
    setBtsProduct({
      ...btsProduct,
      gp,
      exSell,
      incSell,
      total,
      totalIncSell,
      totalExSell,
    });
  };
  const handleQuantityChange = (e) => {
    let quantity = e.target.value === '' ? '' : parseInt(e.target.value, 10);
    if (quantity === '' || isNaN(quantity) || quantity < 0) quantity = '';
    const { exSell, incSell, total, totalIncSell, totalExSell } =
      calculateSellValues(
        btsProduct.gp,
        btsProduct.price,
        quantity,
        btsProduct.conversionFactor,
        btsProduct.taxCode,
        btsProduct.exSell,
        btsProduct.incSell,
      );
    setBtsProduct({
      ...btsProduct,
      quantity,
      exSell,
      incSell,
      total,
      totalIncSell,
      totalExSell,
    });
  };
  const handleHeaderChange = (e) =>
    setBtsProduct({ ...btsProduct, header: e.target.value });
  const handleSeqChange = (e) =>
    setBtsProduct({ ...btsProduct, sequenceNumber: e.target.value });

  // Add or Update Product
  const handleAddProduct = async (e) => {
    e.preventDefault();
    let gpError = false;
    let qtyError = false;
    let seqError = false;
    if (btsProduct.gp === undefined || btsProduct.gp === '') gpError = true;
    if (btsProduct.quantity === undefined || btsProduct.quantity === '')
      qtyError = true;
    // Sequence error: seq must not be more than btsClassProdData.length + 1
    const maxSeq =
      btsClassProdData && Array.isArray(btsClassProdData)
        ? btsClassProdData.length + 1
        : 1;
    if (parseInt(btsProduct.sequenceNumber, 10) > maxSeq) seqError = true;

    setBtsProduct({
      ...btsProduct,
      gpError,
      qtyError,
      seqError,
    });

    if (gpError || qtyError || seqError) return;

    if (!btsProduct.price || btsProduct.price == 0) {
      handleAlert('error', {
        message: 'BTS cost for this product does not exist',
      });
      return;
    }
    if (!selectedClass) {
      handleAlert('error', { message: 'Class is required' });
      return;
    }
    try {
      const payload = {
        ...btsProduct,
        header: btsProduct.header || '',
        headers: btsProduct.header && btsProduct.header.trim() !== ''
          ? (btsProduct.headers && btsProduct.headers.length > 0
              ? btsProduct.headers.map((h, i) => i === 0 ? { ...h, header: btsProduct.header.trim() } : h)
              : [{ header: btsProduct.header.trim() }])
          : [],
      };

      if (btsProduct.id && btsProduct.id > 0) {
        await updateManualProduct(proposalId || id, selectedClass.id, payload);
      } else {
        await addManualProduct(proposalId || id, selectedClass.id, payload);
      }
      await fetchBtsProductsByClass(proposalId || id, selectedClass.id); // refresh table data
      setBtsProduct(btsProductInitialState);
      setSelectedProduct([]);
      if (productTypeaheadRef.current) productTypeaheadRef.current.clear();
    } catch (e) {
      handleAlert('error', e);
    }
  };
  // Cancel
  const handleCancel = () => {
    setBtsProduct(btsProductInitialState);
    setSelectedProduct([]);
    if (productTypeaheadRef.current) productTypeaheadRef.current.clear();
  };

  // File upload
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      handleAlert('error', { message: 'Only .csv files are allowed' });
      setUploadFile(null);
      e.target.value = '';
      return;
    }
    setUploadFile(file);
  };
  const proceedWithUpload = async () => {
    try {
      if (isUploadWizard || !selectedClass) {
        const uploadResp = await uploadSchoolProposalCsvFileHandler(
          uploadFile,
          proposalId || id,
        );
        if (proposalId || id) {
          const data = await getClassListForProposalAPI(proposalId || id);
          setProposalClasses(data || []);
        }

        // Show post-upload report when there are error-skipped or duplicate-skipped rows
        const skipped = uploadResp?.skippedProducts ?? [];
        const duplicateCount = uploadResp?.duplicateRecords ?? 0;
        if (skipped.length > 0 || duplicateCount > 0) {
          openModal(
            <UploadSkippedModal
              insertedCount={uploadResp?.insertedRecords ?? 0}
              duplicateCount={duplicateCount}
              skippedProducts={skipped}
              onClose={closeModal}
            />,
            'Upload Report'
          );
          return; // don't show the generic success alert when the report modal opens
        }
      } else {
        await uploadBtsProductFileByClassHandler(
          uploadFile,
          proposalId || id,
          selectedClass.id,
        );
      }
      handleAlert('success', 'File uploaded and products refreshed');
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      handleAlert('error', e);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) {
      showAlert('error', 'Please select a CSV file first.');
      return;
    }
    setIsVerifying(true);
    try {
      const verifyRes = await verifyProductCsvFile(uploadFile);
      setIsVerifying(false);

      const { existingProducts, nonExistingProducts } = verifyRes || {};

      if (nonExistingProducts && nonExistingProducts.length > 0) {
        openModal(
          <VerificationReportModal
            existingCount={existingProducts?.length || 0}
            nonExistingProducts={nonExistingProducts}
            onConfirm={async () => {
              closeModal();
              await proceedWithUpload();
            }}
            onCancel={closeModal}
          />,
          'Proposal File Verification Report'
        );
      } else {
        await proceedWithUpload();
      }
    } catch (e) {
      setIsVerifying(false);
      handleAlert('error', e);
    }
  };

  const handleCreateProduct = () => {
    openModal(
      <CreateProduct
        closeModal={closeModal}
        prodCode={productQuery}
        isCreatedFromProposal={true}
      />,
      'Add Product',
    );
  };

  const isEditMode = btsProduct.id && btsProduct.id > 0;
  //////////////
  useEffect(() => {
    if (btsClassProdData && !Array.isArray(btsClassProdData)) {
      setBtsProduct((prev) => ({
        ...prev,
        header: btsClassProdData.header || '',
        btsProductId: btsClassProdData.product?.id || 0,
        itemCode: btsClassProdData.product?.itemCode || '',
        description: btsClassProdData.product?.itemDescription || '',
        price: btsClassProdData.product?.btsPrice ?? 0,
        conversionFactor: btsClassProdData.product?.btsConversionFactor || 1,
      }));

      if (btsClassProdData.product) {
        const prod = btsClassProdData.product;
        setSelectedProduct([
          {
            label: `${prod.itemCode} - ${prod.itemDescription} - ${prod.btsPrice ?? 'No BTS Price'}`,
            value: prod.id,
            data: prod,
          },
        ]);
      }
    }
  }, [btsClassProdData, setBtsProduct, setSelectedProduct]);

  return (
    <div className="p-2">
      <div className={`flex w-full pb-2 ${
        btsSchool?.proposal?.booklistType?.toUpperCase() === 'BULK' && divisions.length > 0
          ? 'mb-4'
          : 'mb-6 border-b border-gray-300'
      }`}>
        <div className="w-64">
          <Typeahead
            options={classOptions}
            labelKey="label"
            label="Class"
            onChange={handleClassChange}
            selected={selectedClass ? [selectedClass] : []}
            handleCreateNew={handleCreateClass}
            createNewLabel="Create new class"
            disabledCreateNew={false}
            placeholder="Select or create a class"
            autoComplete="off"
          />
        </div>

        {btsSchool?.proposal?.booklistType === 'Bulk' && (
          <div className="ml-4 w-32">
            <Input
              type="number"
              label="No of Students"
              value={numberOfStudents}
              onChange={handleStudentCountChange}
              onBlur={handleStudentCountBlur}
              disabled={
                !selectedClass ||
                !btsClassProdData ||
                (Array.isArray(btsClassProdData) &&
                  btsClassProdData.length === 0) ||
                (Array.isArray(btsClassProdData) &&
                  btsClassProdData[0] === 'No Data Found')
              }
              min={1}
              style={{ padding: '8px' }}
            />
          </div>
        )}
        <div className="ml-4 min-w-96 ">
          <Typeahead
            options={productOption}
            labelKey="label"
            label="Product"
            onQueryChange={handleSearchChange}
            onChange={handleProductSelect}
            handleCreateNew={handleCreateProduct}
            selected={[]}
            disabledCreateNew={false}
            placeholder="Search and select a product"
            disabled={!selectedClass || btsProduct.btsProductId > 0}
            ref={productTypeaheadRef}
          />
        </div>
        <div className="border-t border-l border-b px-4 py-2 ml-4 rounded-l-xl bg-gray-100/70">
          <Input
            type="file"
            name="proposalFile"
            label={isUploadWizard ? "Upload Proposal file" : "Upload Product file"}
            accept=".csv"
            ref={fileInputRef}
            onChange={handleFileInputChange}
          />
        </div>
        <div className="pt-7 border-t border-r border-b px-4 py-2 rounded-r-xl bg-gray-100/70">
          <Button
            onClick={handleFileUpload}
            disabled={isVerifying}>
            {isVerifying ? 'Verifying...' : 'Upload'}
          </Button>
        </div>
      </div>

      {btsSchool?.proposal?.booklistType?.toUpperCase() === 'BULK' && divisions.length > 0 && (
        <div className="flex flex-wrap gap-4 pb-4 mb-6 items-center border-b border-gray-300 font-400">
          <span className="text-sm text-black mr-2 uppercase tracking-wider font-500">CLASSES:</span>
          <div className="flex flex-wrap gap-3 items-center">
            {divisions.map((div) => (
              <div
                key={div.id}
                className="flex items-center gap-2 px-3 py-1 rounded-md text-sm border bg-white border-gray-300 shadow-sm font-400 text-black"
              >
                <span className="font-400 text-black">{div.divisionName}</span>
                <span className="px-1.5 py-0.5 rounded text-xs bg-gray-200 text-gray-700 font-600">
                  {div.noOfStudents}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {btsProduct.btsProductId > 0 && (
        <form
          className="bg-gray-100 rounded-lg"
          onSubmit={(e) => e.preventDefault()}>
          <div className="w-full bg-gray-300 px-4 font-400 rounded-t-lg py-2">
            <span className="font-800 text-blue-600">
              {'['} {btsProduct.itemCode}
              {'] '}
            </span>
            <span className="font-600">{btsProduct?.description}</span>[
            {btsProduct.unit || 'Unit'}]{' '}
          </div>
          <div className="ml-5 flex gap-4">
            <Input
              className="w-1/2"
              type="text"
              label="header"
              value={btsProduct.header || ''}
              onChange={handleHeaderChange}
            />
            <Input
              type="text"
              label=" Regular Cost"
              value={btsProduct.avgCost || ''}
              disabled
            />
            <Input
              type="text"
              label="BTS Cost"
              value={btsProduct.price / btsProduct.conversionFactor || ''}
              disabled
            />
          </div>
          <div className="grid grid-cols-4 px-5">
            <Input
              type="number"
              step="0.01"
              label="GP %"
              value={btsProduct.gp === undefined ? '' : btsProduct.gp}
              onChange={handleGpChange}
              error={btsProduct.gpError ? 'GP% is required' : undefined}
              required
            />
            <Input
              type="number"
              label="Quantity"
              value={
                btsProduct.quantity === undefined ? '' : btsProduct.quantity
              }
              min={0}
              onChange={handleQuantityChange}
              error={btsProduct.qtyError ? 'Quantity must be 0 or more' : undefined}
              required
            />

            <Input
              type="text"
              label="Seq"
              value={btsProduct.sequenceNumber || ''}
              onChange={handleSeqChange}
              disabled={btsProduct.id == 0}
              error={
                btsProduct.seqError
                  ? `Seq cannot be more than ${btsClassProdData.length + 1}`
                  : undefined
              }
            />
          </div>
          <div className="flex justify-between pb-5 pl-5 pr-5">
            <div className="flex w-full">
              <div className="flex gap-10 text-center pl-5 pt-8">
                <span>
                  Inc-Sell = ${' '}
                  {btsProduct.incSell ? btsProduct.incSell.toFixed(2) : '0.00'}
                </span>

                <span className="flex">
                  Ex-Sell = ${' '}
                  {btsProduct.exSell ? btsProduct.exSell.toFixed(2) : '0.00'}
                </span>
              </div>
            </div>
            <div className="flex gap-4 mt-5">
              <div>
                <Button
                  onClick={handleAddProduct}
                  disabled={!selectedClass || !btsProduct.btsProductId}>
                  {btsProduct.id && btsProduct.id > 0
                    ? 'Update'
                    : 'Add Product'}
                </Button>
              </div>
              <div>
                <Button onClick={handleCancel}>Cancel</Button>
              </div>
            </div>
          </div>
        </form>
      )}
      {selectedClass ? (
        <>
          <div className="container mx-auto mt-10 pr-5">
            <NotesParentsSection
              title="Notes for parents"
              initialValue={notes.note_A}
              onSave={(content) => handleSaveNote('note_A', content)}
              isSaving={isSavingNote.note_A}
            />
          </div>
          <ProposalProductTable
            proposalId={proposalId}
            numberOfStudents={numberOfStudents}
            booklistType={btsSchool?.proposal?.booklistType}
            classId={selectedClass?.id}
          />
          <div className="container mx-auto mt-10 pr-5">
            <NotesParentsSection
              title="Notes for parents"
              initialValue={notes.note_B}
              onSave={(content) => handleSaveNote('note_B', content)}
              isSaving={isSavingNote.note_B}
            />
          </div>
        </>
      ) : (
        <>
          <div className="font-800 text-center my-10">No Class Selected</div>
        </>
      )}
      <div className="flex justify-between mx-10 mt-10">
        <Button onClick={() => exportBTSExcelReport(proposalId)}>
          Export Excel
        </Button>
        <Button
          disabled={
            !selectedClass ||
            (Array.isArray(btsClassProdData) &&
              btsClassProdData[0] === 'No Data Found')
          }
          onClick={() =>
            exportParentsInstructionAPI(
              proposalId,
              selectedClass.id,
              btsSchool?.proposal?.schoolName,
              selectedClass?.className || selectedClass?.label
            )
          }>
          Export Parents Instruction
        </Button>
        <Button
          disabled={
            !selectedClass ||
            (Array.isArray(btsClassProdData) &&
              btsClassProdData[0] === 'No Data Found')
          }
          onClick={() => exportPdfAPI(proposalId, selectedClass.id)}>
          Book list PDF
        </Button>
        {/* {isProposalLoaded && (
          <Button onClick={handleExportShReport} disabled={isShExporting}>
            {isShExporting ? 'Exporting...' : 'Export Step 1'}
          </Button>
        )} */}
      </div>

      {/* Chat Widget - floating bottom-right */}
      {selectedClass &&
        Array.isArray(btsClassProdData) &&
        btsClassProdData.length > 0 &&
        btsClassProdData[0] !== 'No Data Found' && (
          <div className="fixed bottom-4 right-4 z-50">
            <MessageIconButton
              key={selectedClass.id}
              quoteCode={String(btsSchool?.proposal?.quoteId || proposalId || id)}
              classId={selectedClass.id}
            />
          </div>
        )}
    </div>
  );
};

export default ProposalProduct;
